import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';
import { CAMERA_STATE, EXERCISE_STATE, NETWORK_STATE, VOICE_STATE } from '../connection-status';
import {
  AlertIcon,
  CameraIcon,
  CameraOffIcon,
  OfflineIcon,
  ReconnectIcon,
  RestoredIcon,
  VoiceOffIcon,
  WeakSignalIcon,
} from './connection-status-icons';
import { TONE_STYLES } from './connection-status-tones';

const STRIP_STYLES = {
  ok: tw`bg-success_50 border-success_100`,
  warning: tw`bg-warning_light border-[#F6DDB5]`,
  problem: tw`bg-error_50 border-error_100`,
};

const EXERCISE_BANNERS = {
  [EXERCISE_STATE.reconnecting]: {
    tone: 'warning',
    Icon: ReconnectIcon,
    key: 'connectionStatus.exercise.reconnecting',
  },
  [EXERCISE_STATE.restored]: {
    tone: 'ok',
    Icon: RestoredIcon,
    key: 'connectionStatus.exercise.restored',
  },
  [EXERCISE_STATE.failed]: {
    tone: 'problem',
    Icon: AlertIcon,
    key: 'connectionStatus.exercise.failed',
  },
};

// The same reload the camera-disabled info already offers: a reload straight
// onto /exercise is a supported entry, and it opens no connection of its own
// beyond what the page does on any load.
const reloadPage = () => window.location.reload();

const actionButtonStyle = tw`min-h-[44px] shrink-0 rounded-[10px] px-4 text-[13px] font-semibold`;
const primaryActionStyle = [actionButtonStyle, tw`bg-primary text-white`];
const secondaryActionStyle = [actionButtonStyle, tw`border border-stroke bg-white text-secondary`];

const IconBadge = ({ tone, Icon, isStrip }) => (
  <span
    css={[
      tw`flex shrink-0 items-center justify-center rounded-full`,
      isStrip ? tw`h-8 w-8` : tw`h-9 w-9`,
      TONE_STYLES[tone],
    ]}
  >
    {/* No css/tw prop on a component chosen at runtime: the styled-components
        transform hoists it into a module-level styled(Icon), where Icon is not
        in scope. Size the wrapper instead and let the svg fill it. */}
    <span css={isStrip ? tw`h-[18px] w-[18px]` : tw`h-5 w-5`}>
      <Icon width="100%" height="100%" />
    </span>
  </span>
);

const ExerciseBanner = ({ state, isStrip, onOpenDetails }) => {
  const { t } = useTranslation('exercise');
  const banner = EXERCISE_BANNERS[state];

  if (!banner) {
    return null;
  }

  const isFailed = state === EXERCISE_STATE.failed;
  const detailsButton = onOpenDetails && state !== EXERCISE_STATE.restored && (
    <button
      type="button"
      onClick={onOpenDetails}
      css={
        isStrip
          ? tw`min-h-[44px] shrink-0 px-2.5 text-[13px] font-bold text-secondary underline`
          : secondaryActionStyle
      }
    >
      {t(isStrip ? 'connectionStatus.actions.details' : 'connectionStatus.actions.whatsHappening')}
    </button>
  );

  return (
    <div
      css={
        isStrip
          ? [tw`flex flex-col gap-2 border-b px-3 py-2.5`, STRIP_STYLES[banner.tone]]
          : tw`flex max-w-[440px] flex-col gap-2.5 rounded-[16px] bg-white p-2.5 ps-3 shadow-[0_12px_30px_rgba(0,0,0,0.18)]`
      }
    >
      <div tw="flex items-center gap-3">
        <IconBadge tone={banner.tone} Icon={banner.Icon} isStrip={isStrip} />
        <p
          tw="m-0 flex-1 font-semibold leading-[1.4] text-text_primary"
          css={isStrip ? tw`text-[13px]` : tw`text-sm`}
        >
          {t(banner.key)}
        </p>
        {!isFailed && detailsButton}
      </div>
      {isFailed && (
        <div tw="flex flex-wrap gap-2" css={isStrip ? tw`ps-11` : tw`ps-12`}>
          <button type="button" onClick={reloadPage} css={primaryActionStyle}>
            {t('connectionStatus.actions.refresh')}
          </button>
          {detailsButton}
        </div>
      )}
    </div>
  );
};

// Dark translucent pill, same family as the top bar's own controls: a secondary
// notice that must never read as the whole exercise being down.
const SecondaryNotice = ({ Icon, children, isStrip }) => (
  <div
    css={
      isStrip
        ? tw`flex items-start gap-2.5 border-b border-stroke bg-neutral_50 px-3 py-2 text-[13px] font-medium leading-[1.45] text-text_primary`
        : tw`flex max-w-[380px] items-start gap-2.5 rounded-[14px] bg-black/70 py-2 pe-3.5 ps-2.5 text-[13px] font-medium leading-[1.45] text-white shadow-[0_12px_30px_rgba(0,0,0,0.24)]`
    }
  >
    <span
      tw="mt-px h-[18px] w-[18px] shrink-0"
      css={isStrip ? tw`text-[#8A4B00]` : tw`text-[#FFD08A]`}
    >
      <Icon width="100%" height="100%" />
    </span>
    <span>{children}</span>
  </div>
);

const VoiceNotice = ({ state, isStrip }) => {
  const { t } = useTranslation('exercise');

  if (state === VOICE_STATE.reconnecting) {
    return (
      <SecondaryNotice Icon={ReconnectIcon} isStrip={isStrip}>
        {t('connectionStatus.voice.reconnecting')}
      </SecondaryNotice>
    );
  }

  if (state === VOICE_STATE.unavailable) {
    return (
      <SecondaryNotice Icon={VoiceOffIcon} isStrip={isStrip}>
        {t('connectionStatus.voice.unavailable')}
      </SecondaryNotice>
    );
  }

  return null;
};

const CameraNotice = ({ state, isStrip }) => {
  const { t } = useTranslation('exercise');

  if (state === CAMERA_STATE.restarting) {
    return (
      <SecondaryNotice Icon={CameraIcon} isStrip={isStrip}>
        {t('connectionStatus.camera.restarting')}
      </SecondaryNotice>
    );
  }

  if (state !== CAMERA_STATE.unavailable) {
    return null;
  }

  return (
    <div
      css={
        isStrip
          ? [tw`flex flex-col gap-2 border-b px-3 py-2.5`, STRIP_STYLES.warning]
          : tw`flex max-w-[400px] flex-col gap-2.5 rounded-[16px] bg-white p-3 ps-3 shadow-[0_12px_30px_rgba(0,0,0,0.18)]`
      }
    >
      <div tw="flex items-start gap-3">
        <IconBadge tone="warning" Icon={CameraOffIcon} isStrip={isStrip} />
        <div tw="flex-1">
          <p tw="m-0 text-sm font-semibold leading-[1.4] text-text_primary">
            {t('connectionStatus.camera.unavailableTitle')}
          </p>
          <p tw="m-0 mt-1 text-[13px] leading-[1.45] text-[#565C63]">
            {t('connectionStatus.camera.unavailableBody')}
          </p>
        </div>
      </div>
      <div css={isStrip ? tw`ps-11` : tw`ps-12`}>
        <button type="button" onClick={reloadPage} css={primaryActionStyle}>
          {t('connectionStatus.actions.refresh')}
        </button>
      </div>
    </div>
  );
};

// The device has no internet. Replaces the exercise banner while it lasts: the
// cause is known, so "reconnecting" would only be the vaguer half of the story.
// Reconnection itself carries on untouched underneath, and the usual
// "Connection restored" confirms the way back.
const OfflineBanner = ({ isStrip, onOpenDetails }) => {
  const { t } = useTranslation('exercise');

  return (
    <div
      css={
        isStrip
          ? [tw`flex flex-col gap-2 border-b px-3 py-2.5`, STRIP_STYLES.problem]
          : tw`flex max-w-[440px] flex-col gap-2.5 rounded-[16px] bg-white p-3 shadow-[0_12px_30px_rgba(0,0,0,0.18)]`
      }
    >
      <div tw="flex items-start gap-3">
        <IconBadge tone="problem" Icon={OfflineIcon} isStrip={isStrip} />
        <div tw="flex-1">
          <p tw="m-0 text-sm font-semibold leading-[1.4] text-text_primary">
            {t('connectionStatus.network.offlineTitle')}
          </p>
          <p tw="m-0 mt-1 text-[13px] leading-[1.45] text-[#565C63]">
            {t('connectionStatus.network.offlineBody')}
          </p>
        </div>
        {onOpenDetails && (
          <button
            type="button"
            onClick={onOpenDetails}
            css={
              isStrip
                ? tw`min-h-[44px] shrink-0 px-2.5 text-[13px] font-bold text-secondary underline`
                : secondaryActionStyle
            }
          >
            {t(
              isStrip
                ? 'connectionStatus.actions.details'
                : 'connectionStatus.actions.whatsHappening'
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// Measured, not guessed (use-network-quality): the one notice allowed to say
// the problem is on the patient's side, with what they can do about it.
const WeakNetworkNotice = ({ state, isStrip }) => {
  const { t } = useTranslation('exercise');

  if (state !== NETWORK_STATE.weak) {
    return null;
  }

  return (
    <div
      css={
        isStrip
          ? [tw`flex items-start gap-3 border-b px-3 py-2.5`, STRIP_STYLES.warning]
          : tw`flex max-w-[400px] items-start gap-3 rounded-[16px] bg-white p-3 shadow-[0_12px_30px_rgba(0,0,0,0.18)]`
      }
    >
      <IconBadge tone="warning" Icon={WeakSignalIcon} isStrip={isStrip} />
      <div tw="flex-1">
        <p tw="m-0 text-sm font-semibold leading-[1.4] text-text_primary">
          {t('connectionStatus.network.weakTitle')}
        </p>
        <p tw="m-0 mt-1 text-[13px] leading-[1.45] text-[#565C63]">
          {t('connectionStatus.network.weakBody')}
        </p>
      </div>
    </div>
  );
};

/**
 * Everything the patient needs to notice without asking: the exercise
 * connection banner, then voice and camera notices — each separate, so a voice
 * or camera problem never reads as the exercise being offline.
 *
 * `variant="overlay"` stacks floating cards (desktop / landscape, inside the top
 * bar); `variant="strip"` renders full-width in-flow rows (portrait phone,
 * between the camera and the stat bar, so nothing covers the camera).
 * `compact` narrows the overlay for the landscape phone's half-width card.
 *
 * Screen readers: routine changes go through one always-mounted polite region
 * so they are announced once, not re-read on every render; only going offline
 * and the prolonged failure — the states that ask the patient to act — are
 * alerts.
 *
 * Offline wins over everything its absence of internet explains: the exercise
 * banner, the weak-connection notice and the voice notice all step aside.
 * Camera notices stay — a camera problem is not a network one.
 */
const ConnectionStatusStack = ({ status, variant = 'overlay', compact = false, onOpenDetails }) => {
  const { i18n } = useTranslation('exercise');
  const isStrip = variant === 'strip';
  const isOffline = status.network === NETWORK_STATE.offline;
  const isFailed = !isOffline && status.exercise === EXERCISE_STATE.failed;

  return (
    <div
      dir={i18n.dir()}
      css={[
        isStrip ? tw`flex w-full shrink-0 flex-col` : tw`flex flex-col items-end gap-2.5`,
        compact && !isStrip && tw`max-w-[300px]`,
      ]}
    >
      {isOffline && (
        <div role="alert" css={isStrip ? tw`w-full` : undefined}>
          <OfflineBanner isStrip={isStrip} onOpenDetails={onOpenDetails} />
        </div>
      )}
      {isFailed && (
        <div role="alert" css={isStrip ? tw`w-full` : undefined}>
          <ExerciseBanner state={status.exercise} isStrip={isStrip} onOpenDetails={onOpenDetails} />
        </div>
      )}
      <div
        aria-live="polite"
        css={isStrip ? tw`flex w-full flex-col` : tw`flex flex-col items-end gap-2.5`}
      >
        {!isOffline && !isFailed && (
          <ExerciseBanner state={status.exercise} isStrip={isStrip} onOpenDetails={onOpenDetails} />
        )}
        <WeakNetworkNotice state={status.network} isStrip={isStrip} />
        {!isOffline && <VoiceNotice state={status.voice} isStrip={isStrip} />}
        <CameraNotice state={status.camera} isStrip={isStrip} />
      </div>
    </div>
  );
};

export default ConnectionStatusStack;
